import { TRACKED_EVENTS, TrackedEventName } from './events';
import { usePrescriptionStore } from '../../stores/prescriptionStore';
import { isMobileApp } from '../../helpers/isMobileApp';
import { isMobileWeb } from '../../helpers/isMobileWeb';
import { TrackerEventService } from '../../containers/prescription/trackerEventService';
import { logger } from '../../helpers/logger';
import { Medicine, ManualMedicine } from '../../interfaces/medicine';
import { Exam } from '../../interfaces/exam';

export type PlatformType = 'mobileApp' | 'mobileWeb' | 'web';

class TrackingService {
  private isMobile(): PlatformType {
    if (isMobileApp()) {
      return 'mobileApp';
    }

    if (isMobileWeb()) {
      return 'mobileWeb';
    }

    return 'web';
  }

  private getIdMedico(): number {
    const prescription = usePrescriptionStore.getState().prescription;
    return prescription?.Prescritor?.idPrescritor || 0;
  }

  private getIdParceiro(): number {
    const prescription = usePrescriptionStore.getState().prescription;
    return prescription?.Origem?.idParceiro || 0;
  }

  private getTimestamp(): string {
    return new Date().toISOString();
  }

  private getIdPrescricao(): number {
    const prescription = usePrescriptionStore.getState().prescription;
    return prescription?.idPrescricao || 0;
  }

  private getCodigoValidacao(): string {
    const prescription = usePrescriptionStore.getState().prescription;
    return prescription?.CodigoValidacao || '';
  }

  private sendEvent(
    eventName: TrackedEventName,
    metadados: Record<string, any>,
    evento?: string,
    produto?: string
  ): void {
    try {
      const idPrescricao = this.getIdPrescricao();

      if (idPrescricao) {
        const eventData = {
          evento: evento,
          produto: produto || 'Mevo Receita Digital',
          metadados: {
            ...metadados,
            timestamp: metadados.timestamp || this.getTimestamp(),
          },
        };

        TrackerEventService.track(idPrescricao, eventName, eventData);
      }

      if (process.env.NODE_ENV === 'development') {
        logger.log('[Tracking Event]', eventName, {
          evento: evento,
          produto: produto || 'Mevo Receita Digital',
          metadados,
        });
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        logger.error('[Tracking Error]', error);
      }
    }
  }

  trackEvent(eventName: TrackedEventName, customPayload?: Record<string, any>): void {
    const eventData = {
      idMedico: this.getIdMedico(),
      idParceiro: this.getIdParceiro(),
      device: this.isMobile(),
      timestamp: this.getTimestamp(),
      ...customPayload,
    };

    this.sendEvent(eventName, eventData);
  }

  trackPrescriptionIncompletePosologyAdd(idPrescription: number): void {
    const metadados = {
      page: 'prescrição',
      placement: 'main',
      flow: 'prescrição por voz',
      step: 'adicionar posologia incompleta',
      element: 'button',
      action: 'click',
      doctor_id: this.getIdMedico(),
      prescriber_id: this.getIdMedico(),
      CodigoValidacao: this.getCodigoValidacao(),
      partner_id: String(this.getIdParceiro()),
      timestamp: this.getTimestamp(),
      idPrescription,
    };

    this.sendEvent(
      'prescription_incomplete_posology_add_btn_click',
      metadados,
      'VOICE_PRESCRIPTION',
      'Mevo Receita Digital'
    );
  }

  trackPrescriptionItemRemove(item: Medicine | ManualMedicine | Exam): void {
    const isManual = 'idPrescricaoMedicamentoManual' in item;
    const prescription_origin = isManual ? 'manual' : 'base';

    const prescription_method = item.AdicionadoPorIA === true ? 'voice' : 'handwritten';

    let prescription_item = '';
    if ('Label' in item) {
      const medicineItem = item as Medicine | ManualMedicine;
      prescription_item = medicineItem.Posologia
        ? `${medicineItem.Label} - ${medicineItem.Posologia}`
        : medicineItem.Label;
    } else {
      const examItem = item as Exam;
      prescription_item = examItem.Nome || examItem.Label || '';
    }

    const metadados = {
      page: 'prescrição',
      placement: 'main',
      flow: 'prescrição',
      step: 'exclusão de item',
      element: 'button',
      action: 'click',
      doctor_id: this.getIdMedico(),
      prescriber_id: this.getIdMedico(),
      CodigoValidacao: this.getCodigoValidacao(),
      prescription_item,
      prescription_origin,
      prescription_method,
      partner_id: String(this.getIdParceiro()),
      timestamp: this.getTimestamp(),
    };

    this.sendEvent('prescription_item_remove', metadados, 'REMOVE', 'Mevo Receita Digital');
  }

  trackPrescriptionItemEdit(item: Medicine | ManualMedicine | Exam): void {
    const isManual = 'idPrescricaoMedicamentoManual' in item;
    const prescription_origin = isManual ? 'manual' : 'base';

    const prescription_method = item.AdicionadoPorIA === true ? 'voice' : 'handwritten';

    let prescription_item = '';
    if ('Label' in item) {
      const medicineItem = item as Medicine | ManualMedicine;
      prescription_item = medicineItem.Posologia
        ? `${medicineItem.Label} - ${medicineItem.Posologia}`
        : medicineItem.Label;
    } else {
      const examItem = item as Exam;
      prescription_item = examItem.Nome || examItem.Label || '';
    }

    const metadados = {
      page: 'prescrição',
      placement: 'main',
      surface: 'card',
      flow: 'prescrição',
      step: 'edição de item',
      element: 'button',
      action: 'click',
      doctor_id: this.getIdMedico(),
      prescriber_id: this.getIdMedico(),
      CodigoValidacao: this.getCodigoValidacao(),
      prescription_item,
      prescription_origin,
      prescription_method,
      partner_id: String(this.getIdParceiro()),
      timestamp: this.getTimestamp(),
    };

    this.sendEvent('prescription_item_edit', metadados, 'EDIT', 'Mevo Receita Digital');
  }

  trackOfficeMenuSidebarClick(): void {
    const eventData = {
      idMedico: this.getIdMedico(),
      idParceiro: this.getIdParceiro(),
      device: this.isMobile(),
      timestamp: this.getTimestamp(),
      placement: 'side menu',
      surface: 'list',
      flow: 'cadastro b2c via b2b',
      step: 'início',
      element: 'button',
      action: 'click',
    };

    this.sendEvent(TRACKED_EVENTS.OFFICE_SIGN_UP_SIDEBAR_CLICK.name, eventData);
  }

  trackOfficeSendSignupPhoneNumber(): void {
    const eventData = {
      idMedico: this.getIdMedico(),
      idParceiro: this.getIdParceiro(),
      device: this.isMobile(),
      timestamp: this.getTimestamp(),
      placement: 'side menu',
      surface: 'form',
      flow: 'cadastro b2c via b2b',
      step: 'disparar mensagem no whatsapp',
      element: 'button',
      action: 'click',
    };

    this.sendEvent(TRACKED_EVENTS.OFFICE_SEND_SIGNUP_PHONE_NUMBER_CLICK.name, eventData);
  }

  trackOfficeCloseSidebar(): void {
    const eventData = {
      idMedico: this.getIdMedico(),
      idParceiro: this.getIdParceiro(),
      device: this.isMobile(),
      timestamp: this.getTimestamp(),
      placement: 'side menu',
      surface: 'modal',
      flow: 'cadastro b2c via b2b',
      step: 'fechar',
      element: 'button',
      action: 'close',
    };

    this.sendEvent(TRACKED_EVENTS.OFFICE_CLOSE_SIDEBAR_CLICK.name, eventData);
  }

  trackOfficeGoBack(): void {
    const eventData = {
      idMedico: this.getIdMedico(),
      idParceiro: this.getIdParceiro(),
      device: this.isMobile(),
      timestamp: this.getTimestamp(),
      placement: 'side menu',
      surface: 'modal',
      flow: 'cadastro b2c via b2b',
      step: 'link enviado',
      element: 'button',
      action: 'click',
    };

    this.sendEvent(TRACKED_EVENTS.OFFICE_GO_BACK_CLICK.name, eventData);
  }

  trackOfficeSwitchNumber(): void {
    const eventData = {
      idMedico: this.getIdMedico(),
      idParceiro: this.getIdParceiro(),
      device: this.isMobile(),
      timestamp: this.getTimestamp(),
      placement: 'side menu',
      surface: 'modal',
      flow: 'cadastro b2c via b2b',
      step: 'link enviado',
      element: 'button',
      action: 'click',
    };

    this.sendEvent(TRACKED_EVENTS.OFFICE_SWITCH_NUMBER_CLICK.name, eventData);
  }

  trackOfficeResendWhatsappLink(): void {
    const eventData = {
      idMedico: this.getIdMedico(),
      idParceiro: this.getIdParceiro(),
      device: this.isMobile(),
      timestamp: this.getTimestamp(),
      placement: 'side menu',
      surface: 'modal',
      flow: 'cadastro b2c via modal b2b',
      step: 'link enviado',
      element: 'button',
      action: 'click',
    };

    this.sendEvent(TRACKED_EVENTS.OFFICE_RESEND_WHATSAPP_LINK_CLICK.name, eventData);
  }

  trackOfficeConfirmPhoneNumber(): void {
    const eventData = {
      idMedico: this.getIdMedico(),
      idParceiro: this.getIdParceiro(),
      device: this.isMobile(),
      timestamp: this.getTimestamp(),
      placement: 'main',
      surface: 'dialog',
      flow: 'cadastro b2c via b2b',
      step: 'confirmação de número de celular',
      element: 'button',
      action: 'click',
    };

    this.sendEvent(TRACKED_EVENTS.OFFICE_CONFIRM_PHONE_NUMBER_CLICK.name, eventData);
  }

  trackOfficeAlterPhoneNumber(): void {
    const eventData = {
      idMedico: this.getIdMedico(),
      idParceiro: this.getIdParceiro(),
      device: this.isMobile(),
      timestamp: this.getTimestamp(),
      placement: 'main',
      surface: 'dialog',
      flow: 'cadastro b2c via b2b',
      step: 'confirmação de número de celular',
      element: 'button',
      action: 'click',
    };

    this.sendEvent(TRACKED_EVENTS.OFFICE_ALTER_PHONE_NUMBER_CLICK.name, eventData);
  }

  trackConsentDialogClose(): void {
    const eventData = {
      idMedico: this.getIdMedico(),
      idParceiro: this.getIdParceiro(),
      device: this.isMobile(),
      timestamp: this.getTimestamp(),
      placement: 'header',
      surface: 'pop-up',
      flow: 'consentimento e-mail',
      step: 'decidir depois',
      element: 'button',
      action: 'click',
    };

    this.sendEvent(TRACKED_EVENTS.CONSENT_DIALOG_CLOSE.name, eventData);
  }

  trackConsentDialogLater(): void {
    const eventData = {
      idMedico: this.getIdMedico(),
      idParceiro: this.getIdParceiro(),
      device: this.isMobile(),
      timestamp: this.getTimestamp(),
      placement: 'footer',
      surface: 'pop-up',
      flow: 'consentimento e-mail',
      step: 'decidir depois',
      element: 'button',
      action: 'click',
    };

    this.sendEvent(TRACKED_EVENTS.CONSENT_DIALOG_LATER.name, eventData);
  }

  trackConsentDialogConsent(): void {
    const eventData = {
      idMedico: this.getIdMedico(),
      idParceiro: this.getIdParceiro(),
      device: this.isMobile(),
      timestamp: this.getTimestamp(),
      placement: 'footer',
      surface: 'pop-up',
      flow: 'consentimento e-mail',
      step: 'consentir',
      element: 'button',
      action: 'click',
    };

    this.sendEvent(TRACKED_EVENTS.CONSENT_DIALOG_CONSENT.name, eventData);
  }

  trackSncrQuotasLoaded(quotas: string[], featureFlag: boolean): void {
    const metadados = {
      page: 'prescrição',
      placement: 'side menu',
      surface: 'panel',
      flow: 'numeração sncr',
      step: 'listagem de cotas',
      element: 'system',
      action: 'view',
      doctor_id: this.getIdMedico(),
      prescriber_id: this.getIdMedico(),
      CodigoValidacao: this.getCodigoValidacao(),
      partner_id: String(this.getIdParceiro()),
      timestamp: this.getTimestamp(),
      quotas,
      feature_flag: featureFlag,
    };

    this.sendEvent(TRACKED_EVENTS.SNCR_QUOTAS_LOADED.name, metadados, 'SNCR', 'Mevo Receita Digital');
  }
}

export const trackingService = new TrackingService();
